import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "../types";

interface MiniCalendarProps {
  selectedDate: Date;
  onDateSelect: (d: Date) => void;
  onMonthChange: (d: Date) => void;
  events: CalendarEvent[];
}

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildGrid(viewDate: Date): (Date | null)[] {
  const first = startOfMonth(viewDate);
  // Monday-first: weekday 1=Mon..7=Sun
  const startDow = ((first.getDay() + 6) % 7); // 0..6 with 0=Mon
  const daysInMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0,
  ).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function MiniCalendar({
  selectedDate,
  onDateSelect,
  onMonthChange,
  events,
}: MiniCalendarProps) {
  const today = new Date();
  const grid = useMemo(() => buildGrid(selectedDate), [selectedDate]);

  const eventDateKeys = useMemo(() => {
    const set = new Set<string>();
    for (const ev of events) {
      const d = new Date(ev.start);
      set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    }
    return set;
  }, [events]);

  function prevMonth() {
    onMonthChange(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1),
    );
  }
  function nextMonth() {
    onMonthChange(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1),
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[13px] font-semibold text-foreground">
          {selectedDate.toLocaleDateString("en-GB", {
            month: "long",
            year: "numeric",
          })}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={prevMonth}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={nextMonth}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            aria-label="Next month"
          >
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAY_LABELS.map((d, i) => (
          <div
            key={i}
            className="text-center text-[10px] font-semibold uppercase text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {grid.map((d, i) => {
          if (!d)
            return (
              <div
                key={`blank-${i}`}
                className="h-7 w-full"
              />
            );
          const isToday = isSameDay(d, today);
          const isSelected = isSameDay(d, selectedDate);
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const hasEvents = eventDateKeys.has(key);
          return (
            <button
              type="button"
              key={key}
              onClick={() => onDateSelect(d)}
              className={cn(
                "relative h-7 w-full rounded-md text-[11px] transition-colors",
                isSelected
                  ? "bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)] font-semibold"
                  : "text-[hsl(215,16%,30%)] hover:bg-[hsl(210,50%,96%)]",
              )}
            >
              <span
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-full",
                  isToday &&
                    !isSelected &&
                    "bg-[hsl(210,68%,55%)] text-white font-semibold",
                  isToday && isSelected && "bg-[hsl(210,100%,28%)] text-white",
                )}
              >
                {d.getDate()}
              </span>
              {hasEvents && (
                <span
                  className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full"
                  style={{ background: "hsl(210,68%,55%)" }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
